// AGC: Video Capture Modal - Records short videos for lot notes
// AGC: On native Android: launches system camera via hidden file input (capture="camcorder")
// AGC: Max duration: 2 minutes (MAX_VIDEO_DURATION from mediaStorageService)

import React, { useState, useRef, useEffect, useCallback } from 'react'
import { X, Video, Check, Loader2 } from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import { useNoteMetadata } from '../../hooks/useNoteMetadata'
import { NoteMetadataStrip } from './NoteMetadataStrip'
import { MAX_VIDEO_DURATION } from '../../services/mediaStorageService'
import type { GPSLocation } from '../../types/lotNotes'
import toast from 'react-hot-toast'

interface VideoCaptureModalProps {
  isOpen: boolean
  onClose: () => void
  onCapture: (video: Blob, gpsLocation?: GPSLocation, description?: string, taggedUserIds?: string[]) => void
  lotId?: string
}

/**
 * AGC: VideoCaptureModal - Record or pick a video note
 */
export const VideoCaptureModal: React.FC<VideoCaptureModalProps> = ({
  isOpen,
  onClose,
  onCapture,
}) => {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const videoPreviewRef = useRef<HTMLVideoElement>(null)

  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  // AGC: Shared metadata hook — GPS, description, team tags, speech-to-text
  const metadata = useNoteMetadata({ isActive: isOpen })

  // AGC: Open file picker when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => fileInputRef.current?.click(), 300)
    } else {
      cleanup()
    }
    return () => cleanup()
  }, [isOpen])

  const cleanup = useCallback(() => {
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    setCapturedBlob(null)
    setPreviewUrl(null)
  }, [previewUrl])

  // AGC: Handle file selected from native camera or file picker
  const handleFileSelected = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) {
      onClose()
      return
    }
    const url = URL.createObjectURL(file)
    setCapturedBlob(file)
    setPreviewUrl(url)
  }, [onClose])

  // AGC: Save captured video
  const handleSave = useCallback(async () => {
    if (!capturedBlob) return
    setIsSaving(true)
    try {
      const videoBlob = capturedBlob.type.startsWith('video/')
        ? capturedBlob
        : new Blob([capturedBlob], { type: 'video/mp4' })
      onCapture(videoBlob, metadata.gpsLocation, metadata.description, metadata.taggedUserIds)
      handleClose()
    } catch (err) {
      console.error('AGC: Failed to save video', err)
      toast.error(t('notes.videoSaveError') || 'Erro ao salvar vídeo')
    } finally {
      setIsSaving(false)
    }
  }, [capturedBlob, metadata.gpsLocation, metadata.description, metadata.taggedUserIds, onCapture, t])

  const handleClose = useCallback(() => {
    cleanup()
    metadata.reset()
    onClose()
  }, [cleanup, metadata, onClose])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/90">
      <div className="relative w-full h-full max-w-4xl flex flex-col">
        {/* AGC: Header */}
        <div className="flex items-center justify-between px-4 pb-4 pt-safe-4 bg-black/50">
          <h2 className="text-white text-lg font-semibold">
            {capturedBlob ? t('notes.videoPreview') || 'Pré-visualização' : t('notes.recordVideo')}
          </h2>
          <button
            onClick={handleClose}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>

        {/* AGC: Preview area */}
        <div className="flex-1 relative flex items-center justify-center bg-black">
          {previewUrl ? (
            <video
              ref={videoPreviewRef}
              src={previewUrl}
              controls
              autoPlay
              playsInline
              className="max-w-full max-h-full object-contain"
            />
          ) : (
            <div className="text-center p-6">
              <Video className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-white/60 text-sm">
                {t('notes.videoHint') || `Grave um vídeo de até ${MAX_VIDEO_DURATION / 60} minutos`}
              </p>
            </div>
          )}

          {/* AGC: Metadata strip — GPS, description, team tags */}
          <NoteMetadataStrip
            gpsLocation={metadata.gpsLocation}
            gpsLoading={metadata.gpsLoading}
            gpsError={metadata.gpsError}
            onRetryGPS={metadata.captureGPS}
            description={metadata.description}
            onDescriptionChange={metadata.setDescription}
            isListening={metadata.isListening}
            isProcessingSpeech={metadata.isProcessingSpeech}
            onToggleSpeechToText={() => metadata.toggleSpeechToText()}
            taggedUserIds={metadata.taggedUserIds}
            onTaggedUsersChange={metadata.setTaggedUserIds}
            variant="overlay"
          />
        </div>

        {/* AGC: Controls */}
        <div className="px-6 pt-6 pb-safe-4 bg-black/50">
          {capturedBlob ? (
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => { cleanup(); setTimeout(() => fileInputRef.current?.click(), 100) }}
                className="flex-1 max-w-xs py-3 rounded-xl bg-white/10 text-white font-medium text-sm hover:bg-white/20 transition-colors"
              >
                {t('notes.retake') || 'Refazer'}
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex-1 max-w-xs py-3 rounded-xl bg-emerald-600 text-white font-medium text-sm hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-5 h-5" />}
                {t('common.save')}
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-center">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-20 h-20 rounded-full bg-red-500 border-4 border-red-300 hover:scale-110 transition-transform flex items-center justify-center"
              >
                <Video className="w-8 h-8 text-white" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* AGC: Hidden file input — uses native camera on Android */}
      <input
        ref={fileInputRef}
        type="file"
        accept="video/*"
        capture="environment"
        onChange={handleFileSelected}
        className="hidden"
      />
    </div>
  )
}

export default VideoCaptureModal
